import { CaptionEvent, SILENCE_TOKEN } from '../types';
import { EVENT_SCROLL, CLASS_HIDDEN } from '../core/strings';
import { isRtlLanguage, isRtlText } from '../core/rtl';

export interface RendererOptions {
  container: HTMLElement;
  scrollContainer?: HTMLElement;
  fontSize?: 'sm' | 'md' | 'lg' | 'xl';
  theme?: 'dark' | 'light' | 'high-contrast';
  autoScroll?: boolean;
  language?: string;
}

const TAG_SPAN = 'span';
const TAG_P = 'p';
const TAG_DIV = 'div';

const CLASS_CAPTION_FEED = 'caption-feed';
const CLASS_FINALS_CONTAINER = 'finals-container';
const CLASS_INTERIM_TEXT = 'interim-text';
const CLASS_SILENCE_PARAGRAPH = 'caption-paragraph silence-paragraph';
const CLASS_SILENCE_PHRASE = 'silence-phrase';
const CLASS_STATUS_PARAGRAPH = 'caption-paragraph status-paragraph';
const CLASS_STATUS_PHRASE = 'status-phrase';
const CLASS_ACTIVE_PARAGRAPH = 'caption-paragraph active-paragraph';
const CLASS_ACTIVE_PARAGRAPH_STATE = 'active-paragraph';
const CLASS_FINAL_PHRASE = 'final-phrase';

function isHtmlElement(el: unknown): el is HTMLElement {
  return typeof HTMLElement !== 'undefined'
    ? el instanceof HTMLElement
    : Boolean(el && typeof el === 'object' && 'scrollHeight' in (el as any) && 'scrollTop' in (el as any));
}

const BROADCAST_STOPPED_PHRASES = [
  'broadcast has stopped',
  'sändningen har avslutats',
  'la transmisión se ha detenido',
  'die übertragung wurde beendet',
  'la diffusion est arrêtée',
  'a transmissão foi interrompida',
  'la trasmissione è terminata',
  'de uitzending is gestopt',
  'sendingen er stoppet',
  'sendinga er stoppa',
  'udsendelsen er stoppet',
  'lähetys on päättynyt',
  'трансляцію зупинено',
  'трансляция остановлена',
  'transmisja została zatrzymana',
  'vysílání bylo zastaveno',
  'η μετάδοση διακόπηκε',
  'transmisiunea s-a oprit',
  'a közvetítés leállt',
  'yayın durduruldu',
  '配信が終了しました',
  '방송이 종료되었습니다',
  '直播已停止',
  'प्रसारण बंद हो गया है',
  'تم إيقاف البث',
  'השידור הופסק',
  'siaran telah berhenti',
  'siaran telah dihentikan',
  'phát sóng đã dừng',
  'การถ่ายทอดสดหยุดลงแล้ว',
];

export class CaptionRenderer {
  private container: HTMLElement;
  private scrollContainer: HTMLElement | Window;
  private scrollListenerCleanup?: () => void;
  private finalsContainer: HTMLDivElement;
  private interimSpan: HTMLSpanElement;
  private autoScroll: boolean = true;
  private isUserScrolledUp: boolean = false;
  private onScrollLockChange?: (locked: boolean) => void;

  private currentLanguage: string = 'source';
  private isFeedRtl: boolean = false;

  private currentParagraph: HTMLParagraphElement | null = null;
  private lastWasSilence: boolean = false;
  private lastFinalSentence: string = '';
  public currentInterim: string = '';

  constructor(options: RendererOptions, onScrollLockChange?: (locked: boolean) => void) {
    this.container = options.container;
    this.scrollContainer = options.scrollContainer || this.container.parentElement || (typeof window !== 'undefined' ? window : null) as any;
    this.onScrollLockChange = onScrollLockChange;
    if (options.autoScroll !== undefined) {
      this.autoScroll = options.autoScroll;
    }

    this.container.classList.add(CLASS_CAPTION_FEED);
    const fontSize = options.fontSize || 'md';
    this.container.classList.add(`font-${fontSize}`);

    this.currentLanguage = options.language || 'source';
    this.isFeedRtl = isRtlLanguage(this.currentLanguage);
    this.updateFeedDirection();

    this.finalsContainer = document.createElement(TAG_DIV) as HTMLDivElement;
    this.finalsContainer.className = CLASS_FINALS_CONTAINER;

    this.interimSpan = document.createElement(TAG_SPAN);
    this.interimSpan.className = CLASS_INTERIM_TEXT;

    const existingPlaceholder = this.container.querySelector('.live-monitor-placeholder');
    this.container.replaceChildren();
    if (existingPlaceholder) {
      this.container.appendChild(existingPlaceholder);
    }
    this.container.appendChild(this.finalsContainer);

    this.setupScrollListener();
  }

  public setLanguage(lang: string): void {
    if (this.currentLanguage === lang) return;
    this.currentLanguage = lang;
    this.isFeedRtl = isRtlLanguage(lang);
    this.lastFinalSentence = '';
    this.closeActiveParagraph();
    this.updateFeedDirection();
  }

  public getIsRtl(): boolean {
    return this.isFeedRtl;
  }

  private updateFeedDirection(): void {
    if (this.isFeedRtl) {
      this.container.classList.add('is-rtl');
      this.container.setAttribute('dir', 'rtl');
    } else {
      this.container.classList.remove('is-rtl');
      this.container.setAttribute('dir', 'ltr');
    }
  }

  private createParagraph(textSample?: string): HTMLParagraphElement {
    const p = document.createElement(TAG_P);
    p.className = CLASS_ACTIVE_PARAGRAPH;
    const isRtl = this.isFeedRtl || (this.currentLanguage === 'source' && isRtlText(textSample));
    if (isRtl) {
      p.setAttribute('dir', 'rtl');
      p.classList.add('is-rtl');
      if (this.currentLanguage === 'source') {
        this.container.classList.add('is-rtl');
        this.container.setAttribute('dir', 'rtl');
      }
    } else {
      p.setAttribute('dir', 'ltr');
    }
    return p;
  }

  private setupScrollListener(): void {
    const scrollParent = this.scrollContainer || this.container.parentElement || (typeof window !== 'undefined' ? window : null);
    if (!scrollParent) return;

    const checkScroll = () => {
      let scrollBottom = 0;
      if (isHtmlElement(scrollParent)) {
        scrollBottom = scrollParent.scrollHeight - scrollParent.scrollTop - scrollParent.clientHeight;
      } else if (typeof document !== 'undefined') {
        scrollBottom = document.documentElement.scrollHeight - window.scrollY - window.innerHeight;
      }

      // If user scrolled more than 80px above the bottom, consider scrolled up
      const scrolledUp = scrollBottom > 80;
      if (scrolledUp !== this.isUserScrolledUp) {
        this.isUserScrolledUp = scrolledUp;
        this.onScrollLockChange?.(this.isUserScrolledUp);
      }
    };

    if (isHtmlElement(scrollParent)) {
      scrollParent.addEventListener(EVENT_SCROLL, checkScroll, { passive: true });
      this.scrollListenerCleanup = () => scrollParent.removeEventListener(EVENT_SCROLL, checkScroll);
    } else if (typeof window !== 'undefined') {
      window.addEventListener(EVENT_SCROLL, checkScroll, { passive: true });
      this.scrollListenerCleanup = () => window.removeEventListener(EVENT_SCROLL, checkScroll);
    }
  }

  private dismissWelcomeCard(): void {
    if (typeof document === 'undefined') return;
    const welcome = document.getElementById('channel-welcome');
    if (welcome && !welcome.classList.contains(CLASS_HIDDEN)) {
      welcome.classList.add(CLASS_HIDDEN);
    }
    const placeholder = this.container.querySelector('.live-monitor-placeholder');
    if (placeholder) {
      placeholder.remove();
    }
  }

  private isBroadcastStoppedMessage(text: string): boolean {
    const lower = text.toLowerCase();
    return BROADCAST_STOPPED_PHRASES.some((phrase) => lower.includes(phrase));
  }

  handleCaption(event: CaptionEvent): void {
    const rawText = event.text?.trim() || '';
    if (!rawText) return;

    this.dismissWelcomeCard();

    if (event.isFinal) {
      this.currentInterim = '';
      if (this.interimSpan.parentElement) {
        this.interimSpan.remove();
        this.interimSpan.textContent = '';
      }

      // Handle silence events
      if (rawText === SILENCE_TOKEN) {
        if (this.lastWasSilence) return;
        this.lastWasSilence = true;
        this.closeActiveParagraph();

        const silenceP = document.createElement(TAG_P);
        silenceP.className = CLASS_SILENCE_PARAGRAPH;
        if (this.isFeedRtl) {
          silenceP.setAttribute('dir', 'rtl');
        }
        const span = document.createElement(TAG_SPAN);
        span.className = CLASS_SILENCE_PHRASE;
        span.textContent = '—';
        silenceP.appendChild(span);
        this.finalsContainer.appendChild(silenceP);
        this.scrollToBottomIfNeeded();
        return;
      }

      this.lastWasSilence = false;

      // Handle broadcast stopped notification
      if (this.isBroadcastStoppedMessage(rawText)) {
        this.closeActiveParagraph();
        const stopP = document.createElement(TAG_P);
        stopP.className = CLASS_STATUS_PARAGRAPH;
        if (this.isFeedRtl) {
          stopP.setAttribute('dir', 'rtl');
        }
        const span = document.createElement(TAG_SPAN);
        span.className = CLASS_STATUS_PHRASE;
        span.textContent = rawText;
        stopP.appendChild(span);
        this.finalsContainer.appendChild(stopP);
        this.scrollToBottomIfNeeded();
        return;
      }

      // Split across sentence boundaries if multiple sentences arrive in one chunk
      const sentences = rawText.split(/(?<=[.!?]["']?)\s+/);

      for (let i = 0; i < sentences.length; i++) {
        const sentence = sentences[i].trim();
        if (!sentence) continue;

        // Skip consecutive identical sentences (e.g. from upstream model stutters or chunk overlap)
        const norm = sentence.toLowerCase().replace(/[.,!?;:—…"'»”’]+$/u, '').trim();
        if (norm && norm === this.lastFinalSentence) {
          continue;
        }
        if (norm) {
          this.lastFinalSentence = norm;
        }

        // If paragraph was closed by a previous sentence, start a new paragraph
        if (!this.currentParagraph) {
          this.currentParagraph = this.createParagraph(sentence);
          this.finalsContainer.appendChild(this.currentParagraph);
        }

        // Commit the finalized sentence into this paragraph (where interim was playing)
        const span = document.createElement(TAG_SPAN);
        span.className = CLASS_FINAL_PHRASE;
        const hasPriorContent = this.currentParagraph.childNodes.length > 0;
        span.textContent = (hasPriorContent ? ' ' : '') + sentence;
        this.currentParagraph.appendChild(span);

        // Check if this sentence completes the paragraph
        const sentenceEnds = this.isSentenceEnd(sentence);
        const isLastSentenceInChunk = (i === sentences.length - 1);
        const wordCount = (this.currentParagraph.textContent || '').trim().split(/\s+/).length;

        // Close paragraph if sentence has ended, speechFinal signaled a pause, or word count exceeded
        if (sentenceEnds || (isLastSentenceInChunk && event.speechFinal) || wordCount >= 15) {
          this.closeActiveParagraph();
        }
      }
    } else {
      // Interim rapid preview:
      // If previous paragraph was closed (sentence ended), start a new paragraph for this interim
      if (!this.currentParagraph) {
        this.currentParagraph = this.createParagraph(rawText);
        this.finalsContainer.appendChild(this.currentParagraph);
      }

      // Attach interimSpan inside the active paragraph
      if (this.interimSpan.parentElement !== this.currentParagraph) {
        this.currentParagraph.appendChild(this.interimSpan);
      }

      this.currentInterim = rawText;
      const hasPriorContent = this.currentParagraph.childNodes.length > 1 ||
        (this.currentParagraph.firstChild && this.currentParagraph.firstChild !== this.interimSpan);

      this.interimSpan.textContent = (hasPriorContent ? ' ' : '') + rawText;
      this.currentParagraph.classList.add(CLASS_ACTIVE_PARAGRAPH_STATE);
    }

    this.scrollToBottomIfNeeded();
  }

  private isSentenceEnd(text: string): boolean {
    const trimmed = text.trim();
    return /[.!?]["']?$/.test(trimmed) || /[:;—…]$/.test(trimmed);
  }

  private closeActiveParagraph(): void {
    if (this.currentParagraph) {
      if (this.interimSpan.parentElement === this.currentParagraph) {
        this.interimSpan.remove();
        this.interimSpan.textContent = '';
      }
      this.currentParagraph.classList.remove(CLASS_ACTIVE_PARAGRAPH_STATE);
      if (!this.currentParagraph.textContent?.trim()) {
        this.currentParagraph.remove();
      }
      this.currentParagraph = null;
    }
  }

  private scrollToBottomIfNeeded(): void {
    if (this.autoScroll && !this.isUserScrolledUp) {
      this.scrollToBottom();
    }
  }

  scrollToBottom(smooth: boolean = true): void {
    const scrollParent = this.scrollContainer || this.container.parentElement;
    if (isHtmlElement(scrollParent)) {
      scrollParent.scrollTo({
        top: scrollParent.scrollHeight,
        behavior: smooth ? 'smooth' : 'auto',
      });
    } else if (typeof window !== 'undefined') {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: smooth ? 'smooth' : 'auto',
      });
    }
    this.isUserScrolledUp = false;
    this.onScrollLockChange?.(false);
  }

  clear(): void {
    this.lastWasSilence = false;
    this.lastFinalSentence = '';
    this.currentInterim = '';
    this.closeActiveParagraph();
    this.finalsContainer.innerHTML = '';
  }

  destroy(): void {
    if (this.scrollListenerCleanup) {
      this.scrollListenerCleanup();
      this.scrollListenerCleanup = undefined;
    }
    this.closeActiveParagraph();
  }
}
